import {FormProvider, useForm} from 'react-hook-form';
import AurClientFormFields from '../../../client-management/aur-client-form-fields/AurClientFormFields';
import {t} from 'i18next';
import {useDispatch, useSelector} from 'react-redux';
import {
  setAnalyticsInfo,
  setClientInfo,
  setProjectList,
  setTeamMemberPermissionInfo,
} from '../../../../redux/slices/teamMemberSlice';
import {
  IClientInfoFormData,
  ProjectEditFlag,
  ProjectDetails,
  Workspace,
} from '../models';
import {RootState} from '../../../../redux/store';
import {ISelectItem} from '../../../common/aur-select-dropdown/types';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {ClientProject} from '../../../../types/project-type/projectDetailsType';
import {AurOptionProps} from '../../../../types/types';
import {
  getPermissionGroupList,
  getActiveProjectList,
  getProjectByProjectId,
  getHighlyConfidentialProjectsList,
  getDomainWhitelist,
} from '../../../../api/ClientManagementService';
import {getRoles} from '../../../../redux/slices/roleListSlice';
import {addClientInfoValidationSchema} from '../validation-schemas';
import {yupResolver} from '@hookform/resolvers/yup';
import AurButton from '../../../common/aur-button/AurButton';
import {Box, Grid, Typography} from '@mui/material';
import AddTeamMemberAccordion from '../../../user-management/add-new-team-member/AddTeamMemberAccordion';
import ClientProjectSelection from '../../../user-management/add-new-team-member/ClientProjectSelection';
import {ReactComponent as PlusIcon} from '../../../../assests/svg/plus-icon-success.svg';
import styles from '../../../../styles/components/userManagement/clientInfoTab.module.scss';
import {useModal} from '../../../../contexts/modalContext/ModalContext';
import AurLoadingIndicator from '../../../common/aur-loading-indicator/AurLoadingIndictor';
import AurHighlyConfidential from '../../../common/aur-highly-confidential/AurHighlyConfidential';
import {useToast} from '../../../../contexts/toastContext/ToastContext';
import {UserRoleIds} from '../../../../types/user-management/userManagementTypes';
import {PermissionGroup} from '../../../../types/permission-group/permissionGroup';
import {Permission} from '../../../../types/permission-group/permission';
import {
  clearNewlyAddedPermissionGroup,
  permissionGroupSelector,
} from '../../../../redux/slices/permissionGroupSlice';
import {
  isNewlyAddedPermissionGroupForClient,
  mergePermissionGroupsFromRedux,
  permissionGroupToItemType,
  upsertPermissionGroupItem,
} from '../../../../utils/permissionGroupItemUtils';
import {store} from '../../../../redux/store';
import {
  ECA_PERMISSION_CODES_CONSTANTS,
  EVIDENCE_SOURCE_MANAGEMENT,
  PERMISSION_GROUP_CATEGORY,
  PERMISSION_TYPE,
} from '../../../../constants/constants';
import {
  checkEcaEnabledByProjectLevel,
  getPermissionDetail,
  getPermissionDetailById,
  getWorkspacesWithDisplayValue,
} from './utils';
import {filter, identity, isEmpty} from 'lodash';
import {EstimatedBudgetValue} from '../../../permission-groups/components/EstimatedBudgetView';
import {EcaPermissionItemValue} from '../../../user-management/add-new-team-member/EcaPermissionItem';
import {getECAPermissions} from '../../../../redux/slices/permissionsSlice';
import {UserRolesData} from '../../../../redux/slices/userRoleSlice';
import AurDialog from '../../../common/aur-dialog/AurDialog';
import * as psl from 'psl';
import {EvidenceSourceManageValue} from '../../../permission-groups/components/EvidenceSourceManageView';
import AurIcon, {IconNames} from '../../../common/aur-icon/AurIcon';
import {
  GA_CLICK_TYPES,
  GA_EVENT_NAMES,
  GA_REGIONS,
  GA_SUBPAGE_TYPE,
} from '../../../../constants/analyticsConstants';
import {useAnalytics} from '../../../../hooks/useAnalytics';

type TeamMemberInfoProps = {
  activeStep: number;
  setActiveStep: (value: number) => void;
  isEditMode?: boolean;
  showHCProjects: boolean;
  setShowHCProjects: (value: boolean) => void;
  onCreateNewPermissionGroup: (
    clientId: string,
    category: PERMISSION_GROUP_CATEGORY
  ) => void;
  onGetBudgetConsumptionEnabled?: (budgetConsumptionEnabled: boolean) => void;
};

export interface ItemType {
  ItemKey: string;
  ItemName: string;
  ItemDescription: string;
  ItemFeatures: string[];
  ItemFeaturesForECI: string[];
  ItemFeaturesForBudget: string[];
  permissions: Permission[];
  ItemEvidenceSourceManagement: string[];
  ItemFeaturesForSystem: string[];
  ItemFeaturesForManageProject: string[];
  ItemFeaturesForBusinessIntelligence: string[];
}

type DomainWhitelistItem = {
  clientDomainId: string;
  clientId: string;
  domainName: string;
  isDefault: boolean;
};

const AddClientInfo = (props: TeamMemberInfoProps) => {
  const {
    activeStep,
    setActiveStep,
    showHCProjects,
    setShowHCProjects,
    onCreateNewPermissionGroup,
    onGetBudgetConsumptionEnabled,
  } = props;
  const {showToast} = useToast();

  // multiple project state --------------------------------------------------------------------------------
  const [unsavedProjectList, setUnsavedProjectList] = useState<
    ProjectDetails[]
  >([]);

  const [openDeleteModal, setOpenDeleteModal] = useState(false);

  const [projectEdit, setProjectEdit] = useState<ProjectEditFlag>();
  const [adminRoleSelected, setAdminRoleSelected] = useState<boolean>(false);
  const [selectedClientPermissionId, setSelectedClientPermissionId] =
    useState('');
  const [selectedProjectPermissionId, setSelectedProjectPermissionId] =
    useState('');
  const savedProjectList = useSelector(
    (state: RootState) => state.teamMember.projectList
  );
  const [workspaceOptions, setWorkspaceOptions] = useState<ISelectItem[]>([]);
  const [clientProjects, setClientProjects] = useState<ClientProject[]>([]);
  const [clientHCProjects, setClientHCProjects] = useState<ClientProject[]>([]);
  const [projects, setProjects] = useState<AurOptionProps[]>([]);
  const [currentTeamMemberclients, setCurrentTeamMemberclients] = useState<
    string[]
  >([]);
  const [clientPermissionGroups, setClientPermissionGroups] = useState<
    ItemType[]
  >([]);
  const [projectPermissionGroups, setProjectPermissionGroups] = useState<
    ItemType[]
  >([]);
  const [clientId, setClientId] = useState('');
  const roleList = useSelector(getRoles);
  const userRoles = useSelector((state: RootState) => state.userRole);
  const [teamMemberRole, setTeamMemberRole] = useState('');
  const {trackGAEvent} = useAnalytics();

  const {clientInfo: clientInfoData} = useSelector(
    (state: RootState) => state.teamMember
  );
  const teamMemberInfo = useSelector(
    (state: RootState) => state.teamMember.teamMemberInfo
  );
  const [isGetClientsAsLookupLoading, setIsGetClientsAsLookupLoading] =
    useState(false);
  const [isGetPermissionGroupListLoading, setIsGetPermissionGroupListLoading] =
    useState(false);
  const [
    isGetClientProjectsByClientIdLoading,
    setIsGetClientProjectsByClientIdLoading,
  ] = useState(false);
  const [clientInfoFormData, setClientInfoFormData] = useState({
    clientId: clientInfoData.clientId,
    clientName: clientInfoData.client,
    role: clientInfoData.roleId,
    project: clientInfoData.project,
    workspaces: clientInfoData.workspaces,
    permissionGroup: clientInfoData.permissionGroup,
    permissionGroupId: clientInfoData.permissionGroupId,
    budgetConsumptionEnabled: clientInfoData.budgetConsumptionEnabled,
  });
  const [evidenceSourcePermissions] = useState<EvidenceSourceManageValue>({
    manageProjectEvidence: false,
    uploadRights: false,
    initiateProcessingRights: false,
  });
  const [
    clientBudgetEstimatedPermisionEnable,
    setClientBudgetEstimatedPermisionEnable,
  ] = useState(false);
  const [budgetEstimated, setBudgetEstimated] = useState<EstimatedBudgetValue>({
    rightToViewEstimatedBudget: false,
    showBudgetAlerts: false,
  });
  const [selectedEcaPermission, setSelectedEcaPermission] =
    useState<EcaPermissionItemValue>({
      ecaPermissionId: '',
      createDocumentPermissionEnabled: false,
    });

  const [showClientProjectSelection, setShowClientProjectSelection] =
    useState<boolean>(false);

  const dispatch = useDispatch();
  const ecaPermissions = useSelector(getECAPermissions);
  const [, setClientPermissionGroupsRef] = useState<PermissionGroup[]>([]);
  const [projectPermissionGroupsRef, setProjectPermissionGroupsRef] = useState<
    PermissionGroup[]
  >([]);
  const [showClientAdminModal, setShowClientAdminModal] =
    useState<boolean>(false);

  useEffect(() => {
    fetchTeamMemberClients();

    if (clientInfoFormData.clientId) setClientId(clientInfoFormData.clientId);

    if (clientInfoFormData.role) setTeamMemberRole(clientInfoFormData.role);

    if (clientInfoFormData.permissionGroupId && !adminRoleSelected)
      setSelectedClientPermissionId(clientInfoFormData.permissionGroupId);

    if (clientInfoFormData.budgetConsumptionEnabled)
      setClientBudgetEstimatedPermisionEnable(
        clientInfoFormData.budgetConsumptionEnabled
      );
  }, []);

  const selectedProjectPermissionGroup = useMemo(
    () =>
      projectPermissionGroupsRef?.find(
        (data: PermissionGroup) =>
          data.permissionGroupId === selectedProjectPermissionId
      ),
    [projectPermissionGroupsRef, selectedProjectPermissionId]
  );

  const refreshPermissionGroups = useSelector(
    (state: RootState) => state.permissionGroups.refreshPermissionGroups
  );
  const newlyAddedPermissionGroupId = useSelector(
    (state: RootState) => state.permissionGroups.newlyAddedPermissionGroupId
  );
  const newlyAddedPermissionGroupCategory = useSelector(
    (state: RootState) =>
      state.permissionGroups.newlyAddedPermissionGroupCategory
  );
  const permissionGroupsState = useSelector(permissionGroupSelector);

  useEffect(() => {
    (async () => {
      const options = await getWorkspaceOptions(
        clientInfoFormData?.project || ''
      );
      const workspaceData = getWorkspacesWithDisplayValue(
        options as unknown as Workspace[]
      );
      setWorkspaceOptions(workspaceData as unknown as ISelectItem[]);
    })();
  }, [clientInfoFormData?.project, projects.length]);

  useEffect(() => {
    if (showHCProjects) {
      if (adminRoleSelected)
        setProjects(formatProjectOptions(clientHCProjects));
      else setProjects(formatProjectOptions(clientProjects));
    } else {
      setProjects(
        formatProjectOptions(
          clientProjects.filter((project) => !project.highlyConfidential)
        )
      );
    }
  }, [showHCProjects, adminRoleSelected, clientHCProjects]);

  const separatePermissions = (permissions: Permission[], type: string) => {
    return permissions
      .filter((perm: Permission) => perm.type === type)
      .map((perm: Permission) => perm.permissionCode);
  };
  const fetchPermissionGroups = async (selectedClientId: string) => {
    if (selectedClientId === '') return;
    else {
      setIsGetPermissionGroupListLoading(true);
      try {
        const result = await getPermissionGroupList(
          selectedClientId,
          PERMISSION_GROUP_CATEGORY.CLIENT
        );
        const apiGroups = result.data.data.permissionGroups;
        if (!apiGroups) {
          setClientPermissionGroups([]);
          return;
        }

        const permissionGroups = mergePermissionGroupsFromRedux(
          apiGroups,
          store.getState().permissionGroups,
          selectedClientId,
          PERMISSION_GROUP_CATEGORY.CLIENT
        );

        const permissionGroupOptionList: ItemType[] = permissionGroups.map(
          (group: PermissionGroup) => ({
            ItemKey: group.permissionGroupId,
            ItemName: group.name,
            ItemDescription: group.description || '',
            ItemFeatures: separatePermissions(
              group.permissions,
              PERMISSION_TYPE.REPORTS
            ),
            ItemFeaturesForECI: separatePermissions(
              group.permissions,
              PERMISSION_TYPE.ECI
            ),
            ItemFeaturesForBudget: separatePermissions(
              group.permissions,
              PERMISSION_TYPE.BUDGET
            ),
            ItemEvidenceSourceManagement: separatePermissions(
              group.permissions,
              PERMISSION_TYPE.EVIDENCE
            ),
            ItemFeaturesForSystem: separatePermissions(
              group.permissions,
              PERMISSION_TYPE.SYSTEM
            ),
            ItemFeaturesForManageProject: separatePermissions(
              group.permissions,
              PERMISSION_TYPE.MANAGE_PROJECT
            ),
            ItemFeaturesForBusinessIntelligence: separatePermissions(
              group.permissions,
              PERMISSION_TYPE.BUSINESS_INTELLIGENCE
            ),
            permissions: group.permissions,
          })
        );
        setClientPermissionGroupsRef(permissionGroups);
        setClientPermissionGroups(permissionGroupOptionList);
        fetchProjectPermissionGroups(selectedClientId);
      } catch (error) {
        console.error('getPermissionForPermissionGroupSelect:', error);
      } finally {
        setIsGetPermissionGroupListLoading(false);
      }
    }
  };
  const fetchProjectPermissionGroups = async (selectedClientId: string) => {
    if (selectedClientId === '') return;
    else {
      setIsGetPermissionGroupListLoading(true);
      try {
        const result = await getPermissionGroupList(
          selectedClientId,
          PERMISSION_GROUP_CATEGORY.PROJECT
        );
        const apiGroups = result.data.data.permissionGroups;
        if (!apiGroups) {
          setProjectPermissionGroups([]);
          return;
        }

        const permissionGroups = mergePermissionGroupsFromRedux(
          apiGroups,
          store.getState().permissionGroups,
          selectedClientId,
          PERMISSION_GROUP_CATEGORY.PROJECT
        );

        const permissionGroupOptionList: ItemType[] = permissionGroups.map(
          (group: PermissionGroup) => ({
            ItemKey: group.permissionGroupId,
            ItemName: group.name,
            ItemDescription: group.description || '',
            ItemFeatures: separatePermissions(
              group.permissions,
              PERMISSION_TYPE.REPORTS
            ),
            ItemFeaturesForECI: separatePermissions(
              group.permissions,
              PERMISSION_TYPE.ECI
            ),
            ItemFeaturesForBudget: separatePermissions(
              group.permissions,
              PERMISSION_TYPE.BUDGET
            ),
            ItemEvidenceSourceManagement: separatePermissions(
              group.permissions,
              PERMISSION_TYPE.EVIDENCE
            ),
            ItemFeaturesForSystem: separatePermissions(
              group.permissions,
              PERMISSION_TYPE.SYSTEM
            ),
            ItemFeaturesForManageProject: separatePermissions(
              group.permissions,
              PERMISSION_TYPE.MANAGE_PROJECT
            ),
            ItemFeaturesForBusinessIntelligence: separatePermissions(
              group.permissions,
              PERMISSION_TYPE.BUSINESS_INTELLIGENCE
            ),
            permissions: group.permissions,
          })
        );
        setProjectPermissionGroupsRef(permissionGroups);
        setProjectPermissionGroups(permissionGroupOptionList);
      } catch (error) {
        console.error('getPermissionForPermissionGroupSelect:', error);
      } finally {
        setIsGetPermissionGroupListLoading(false);
      }
    }
  };

  const fetchTeamMemberClients = async () => {
    try {
      if (userRoles) {
        const teamMemberclientIds = userRoles.userRoles.map(
          (userRole: UserRolesData) => userRole.client.clientId
        );
        setCurrentTeamMemberclients(teamMemberclientIds);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const fetchHCProjects = async () => {
    try {
      const response = await getHighlyConfidentialProjectsList(clientId);
      setClientHCProjects(response.data.data.data);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    if (refreshPermissionGroups) {
      fetchPermissionGroups(clientId);
    }
  }, [refreshPermissionGroups]);

  useEffect(() => {
    if (
      !newlyAddedPermissionGroupId ||
      !newlyAddedPermissionGroupCategory ||
      !isNewlyAddedPermissionGroupForClient(permissionGroupsState, clientId)
    ) {
      return;
    }

    const newGroup = permissionGroupsState.permissionGroups.find(
      (group) =>
        group.permissionGroupId === newlyAddedPermissionGroupId &&
        group.clientId === clientId &&
        group.category === newlyAddedPermissionGroupCategory
    );
    if (newGroup) {
      const item = permissionGroupToItemType(newGroup);
      if (
        newlyAddedPermissionGroupCategory == PERMISSION_GROUP_CATEGORY.CLIENT
      ) {
        setClientPermissionGroups((prev) =>
          upsertPermissionGroupItem(prev, item)
        );
        setClientPermissionGroupsRef((prev) => {
          if (
            prev.some(
              (group) => group.permissionGroupId === newGroup.permissionGroupId
            )
          ) {
            return prev;
          }
          return [...prev, newGroup].sort((a, b) =>
            a.name.toLowerCase().localeCompare(b.name.toLowerCase())
          );
        });
      } else {
        setProjectPermissionGroups((prev) =>
          upsertPermissionGroupItem(prev, item)
        );
        setProjectPermissionGroupsRef((prev) => {
          if (
            prev.some(
              (group) => group.permissionGroupId === newGroup.permissionGroupId
            )
          ) {
            return prev;
          }
          return [...prev, newGroup].sort((a, b) =>
            a.name.toLowerCase().localeCompare(b.name.toLowerCase())
          );
        });
      }
    }
    if (newlyAddedPermissionGroupCategory == PERMISSION_GROUP_CATEGORY.CLIENT) {
      setSelectedClientPermissionId(newlyAddedPermissionGroupId);
    } else {
      if (clientInfoFormData.project) {
        setSelectedProjectPermissionId(newlyAddedPermissionGroupId);
      }
    }
    dispatch(clearNewlyAddedPermissionGroup());
  }, [
    newlyAddedPermissionGroupId,
    newlyAddedPermissionGroupCategory,
    permissionGroupsState.permissionGroups,
    clientId,
  ]);

  useEffect(() => {
    if (clientId) {
      fetchHCProjects();
      fetchPermissionGroups(clientId);
      fetchProjects(clientId);
    }
  }, [clientId]);

  const getWorkspaceOptions = async (
    selectedProjectId: string
  ): Promise<ISelectItem[]> => {
    if (selectedProjectId === '') return [];

    try {
      const response = await getProjectByProjectId(selectedProjectId);
      return response.data?.data?.workspaces ?? [];
    } catch {
      return [];
    }
  };

  const resetBudgetEstimated = useCallback(() => {
    setBudgetEstimated({
      rightToViewEstimatedBudget: false,
      showBudgetAlerts: false,
    });
  }, []);

  const handleProjectSelection = async (selectedProjectId: string) => {
    setClientInfoFormData((prev) => ({
      ...prev,
      project: selectedProjectId,
    }));
    resetBudgetEstimated();
  };

  const fetchProjects = async (selectedClientId: string) => {
    if (selectedClientId === '') return;
    else {
      setIsGetClientProjectsByClientIdLoading(true);
      try {
        const response = await getActiveProjectList(selectedClientId);
        setClientProjects(response.data?.data?.data);
        setProjects(formatProjectOptions(response.data?.data?.data));
      } catch (error) {
        console.error(error);
      } finally {
        setIsGetClientProjectsByClientIdLoading(false);
      }
    }
  };

  const methods = useForm({
    defaultValues: {
      client: clientInfoData.clientId,
      role: clientInfoData.roleId,
      permissionGroup: clientInfoData.permissionGroupId,
      project: clientInfoData.project,
      workspaces: clientInfoData.workspaces,
    },
    mode: 'all',
    resolver: yupResolver(addClientInfoValidationSchema()),
  });

  const {
    handleSubmit,
    formState: {isValid},
    control,
  } = methods;

  const isEcaEnabledByProjectLevel = useMemo(() => {
    return checkEcaEnabledByProjectLevel(
      clientProjects,
      clientHCProjects,
      clientInfoFormData.project
    );
  }, [clientHCProjects, clientInfoFormData.project, clientProjects]);

  const onSubmit = (data: IClientInfoFormData) => {
    const projects = adminRoleSelected ? clientHCProjects : clientProjects;
    const currentProject = projects.find(
      (x) => x.projectId.toLowerCase() === data.project?.toLowerCase()
    );
    const projectName = currentProject?.projectName ?? '';
    const selectedClientPermissionGroup = clientPermissionGroups.find(
      (x) => x.ItemKey === data.permissionGroup
    );
    const selectedProjectPermissionGroupItem = projectPermissionGroups.find(
      (x) => x.ItemKey === data.projPermissionGroup
    );
    const permissionGroupName = adminRoleSelected
      ? ''
      : selectedClientPermissionGroup?.ItemName ?? '';
    let projectPermissionGroupName = adminRoleSelected
      ? ''
      : selectedProjectPermissionGroupItem?.ItemName ?? '';

    if (
      !adminRoleSelected &&
      data.projPermissionGroup !== '' &&
      projectPermissionGroupName === ''
    ) {
      const reduxProjectPermissionGroup =
        permissionGroupsState.permissionGroups.find(
          (group) =>
            group.permissionGroupId === data.projPermissionGroup &&
            group.category === PERMISSION_GROUP_CATEGORY.PROJECT &&
            group.clientId === clientId
        );
      projectPermissionGroupName = reduxProjectPermissionGroup?.name ?? '';
    }
    const fullData = {
      roleId: data.role,
      clientId: data.client,
      client: clientInfoFormData.clientName,
      permissionGroupId: adminRoleSelected ? '' : data.permissionGroup,
      permissionGroup: permissionGroupName,
      project: data.project,
      projectId: data.project?.toLowerCase() ?? '',
      projectName: projectName,
      projPermissionGroupId: adminRoleSelected ? '' : data.projPermissionGroup,
      projPermissionGroup: projectPermissionGroupName,
      workspaces: data.workspaces,
      domainCountryCode: currentProject?.domainCountryCode as string,
      dataCenterName: currentProject?.dataCenterName as string,
      countryName: currentProject?.countryName as string,
    };
    if (fullData) {
      setClientInfoFormData({
        clientId: fullData.clientId,
        clientName: fullData.client,
        permissionGroup: fullData.permissionGroup,
        permissionGroupId: fullData.permissionGroupId,
        role: fullData.roleId,
        project: '',
        workspaces: [],
        budgetConsumptionEnabled: clientBudgetEstimatedPermisionEnable,
      });
      const project: ProjectDetails = {
        projectId: fullData.projectId,
        projectName: fullData.projectName,
        highlyConfidential: clientHCProjects.some(
          (x) =>
            x?.projectId?.toLowerCase() === fullData?.projectId?.toLowerCase()
        ),
        manageProjectEvidence: evidenceSourcePermissions.manageProjectEvidence,
        uploadRights: evidenceSourcePermissions.uploadRights,
        initiateProcessingRights:
          evidenceSourcePermissions.initiateProcessingRights,
        workspaces:
          data.workspaces?.map((item) => {
            return {
              workspaceId: item['workspaceId'],
              workspaceName: item['workspaceName'],
              workspaceCode: item['workspaceCode'],
            } as Workspace;
          }) ?? [],
        domainCountryCode: fullData.domainCountryCode,
        dataCenterName: fullData.dataCenterName,
        countryName: fullData.countryName,
        permissionGroup: fullData?.projPermissionGroup,
        permissionGroupId: fullData?.projPermissionGroupId,
      };
      project.permissionCodeBudgetConsumption =
        budgetEstimated.rightToViewEstimatedBudget;
      project.permissionCodeBudgetAlert = budgetEstimated.showBudgetAlerts;

      const projectPermissions: Permission[] = [];
      if (
        !isEmpty(selectedEcaPermission.ecaPermissionId) &&
        isEcaEnabledByProjectLevel
      ) {
        projectPermissions.push(
          getPermissionDetailById(
            selectedEcaPermission.ecaPermissionId,
            ecaPermissions
          ) as never
        );
      }

      if (
        selectedEcaPermission.createDocumentPermissionEnabled === true &&
        isEcaEnabledByProjectLevel
      ) {
        projectPermissions.push(
          getPermissionDetail(
            ECA_PERMISSION_CODES_CONSTANTS.ECA_CREATE_DOCUMENT,
            ecaPermissions
          ) as never
        );
      }
      if (evidenceSourcePermissions.manageProjectEvidence) {
        projectPermissions.push(
          getPermissionDetail(
            EVIDENCE_SOURCE_MANAGEMENT.STAGING_AREA_ACCESS,
            selectedProjectPermissionGroup?.permissions
          ) as never
        );
      }

      if (evidenceSourcePermissions.uploadRights) {
        projectPermissions.push(
          getPermissionDetail(
            EVIDENCE_SOURCE_MANAGEMENT.EVIDENCE_SOURCE_UPLOAD_ACCESS,
            selectedProjectPermissionGroup?.permissions
          ) as never
        );
      }

      if (evidenceSourcePermissions.initiateProcessingRights) {
        projectPermissions.push(
          getPermissionDetail(
            EVIDENCE_SOURCE_MANAGEMENT.INITIATE_PROCESSING_ACCESS,
            selectedProjectPermissionGroup?.permissions
          ) as never
        );
      }
      project.projectPermissions = filter(
        projectPermissions,
        identity
      ) as Permission[];

      setUnsavedProjectList([project]);
      setClientInfoFormData((prev) => ({
        ...prev,
        project: '',
      }));
      resetBudgetEstimated();
    }
    showToast(
      t('addEditTeamMember.projectAddSuccess'),
      'success',
      undefined,
      'center'
    );
  };

  const roleOptionList = useMemo(
    () =>
      (roleList ?? []).map((item) => ({
        key: item?.id,
        value: item?.id,
        label: item?.name,
        roleCode: item?.roleCode,
      })),
    [roleList]
  );

  // ------------------------------------------------------------------------------------
  const editClickHandler = (projectEditFlag: ProjectEditFlag) => {
    setProjectEdit(projectEditFlag);
  };
  const deleteClickHandler = (projectId: string) => {
    // modal logic
    setUnsavedProjectList((pv) => {
      return pv.filter((p) => p.projectId != projectId);
    });
  };

  const handleConfirmDelete = () => {
    setOpenDeleteModal(false);
    continueSaveAndNavigate();
  };

  const handleCancelDelete = () => {
    setOpenDeleteModal(false);
  };

  function getDomainFromEmail(email: string | null): string | null {
    if (!email) return null;

    const atIndex = email.indexOf('@');
    if (atIndex === -1) return null;

    // take everything after @
    const domain = email
      .slice(atIndex + 1)
      .trim()
      .toLowerCase();

    const parsed = psl.parse(domain);

    if (parsed && typeof parsed === 'object' && 'domain' in parsed) {
      return parsed.domain ?? null;
    }

    return null;
  }

  function getBaseDomain(hostname: string): string {
    const parsed = psl.parse(hostname);
    if ('domain' in parsed && parsed.domain) {
      return parsed.domain;
    }
    return hostname;
  }

  const continueSaveAndNavigate = () => {
    dispatch(setProjectList(unsavedProjectList));
    if (checkIsUserExist(clientId)) return;

    let permissionName = '';
    if (adminRoleSelected) {
      permissionName = clientPermissionGroups[0]?.ItemName ?? '';
    } else if (selectedClientPermissionId) {
      const filteredPermissionGroup = clientPermissionGroups?.find(
        (p) => p.ItemKey == selectedClientPermissionId
      );
      permissionName = filteredPermissionGroup?.ItemName ?? '';
    }
    dispatch(
      setClientInfo({
        clientId: clientId,
        client: clientInfoFormData.clientName,
        roleId: teamMemberRole,
        role: roleList?.find((r) => r.id == teamMemberRole)?.name ?? '',
        permissionGroupId: adminRoleSelected ? '' : selectedClientPermissionId,
        permissionGroup: permissionName,
        budgetConsumptionEnabled: clientBudgetEstimatedPermisionEnable,
      })
    );
    dispatch(
      setTeamMemberPermissionInfo({
        clientPermList: clientPermissionGroups,
        projPermList: projectPermissionGroups,
      })
    );

    setActiveStep(activeStep + 1);
  };

  const saveAndContinueHandler = async () => {
    const roleName = roleList?.find((r) => r.id == teamMemberRole)?.name ?? '';
    dispatch(
      setAnalyticsInfo({
        userRole: roleName || '',
        numberOfProject: `${unsavedProjectList?.length || ''}`,
      })
    );
    const userDomain = getDomainFromEmail(teamMemberInfo.email);

    const response = await getDomainWhitelist(
      clientInfoFormData.clientId as string
    );
    const whitelistItems: DomainWhitelistItem[] = response?.data?.data || [];

    const domainList: string[] = whitelistItems
      .map((item) => getBaseDomain(item.domainName))
      .filter((domain): domain is string => domain !== null);

    const clientExists = userDomain ? domainList.includes(userDomain) : false;

    if (!adminRoleSelected) {
      if (!clientExists) {
        console.log('Domain not whitelisted');
        setOpenDeleteModal(true);
        return; // stop here =>   wait for user action
      }
    }

    console.log('Domain is whitelisted');
    continueSaveAndNavigate();
  };

  const formatProjectOptions = (projects: ClientProject[]) => {
    return projects.map((project: ClientProject) => {
      if (project.highlyConfidential) {
        return {
          key: project.projectId,
          value: project.projectId,
          label: project.projectName,
          displayElement: (
            <div className="HCProject">
              {project.projectName}
              <AurHighlyConfidential size="small" type="pill" />
            </div>
          ),
          ...project,
        };
      } else {
        return {
          key: project.projectId,
          value: project.projectId,
          label: project.projectName,
          ...project,
        };
      }
    });
  };

  const isSaveButtonEnabled = () => {
    if (isInUnsavedMode()) return false;
    if (adminRoleSelected && clientId && !showHCProjects) return true;
    if (unsavedProjectList.length > 0) return true;
    if (!adminRoleSelected && selectedClientPermissionId != '') return true;

    return false;
  };
  useEffect(() => {
    setUnsavedProjectList(savedProjectList ?? []);
    setAdminRoleSelected(clientInfoFormData.role === UserRoleIds.ClientAdmin);
  }, [activeStep]);

  // Add and Update project
  const addThisProjectClickHandler = (
    project: ProjectDetails,
    prevProjectId = ''
  ) => {
    setUnsavedProjectList((pv) => {
      const index = pv.findIndex(
        (proj) => proj.projectId.toLowerCase() === prevProjectId.toLowerCase()
      );

      if (index !== -1) {
        // update the project with new value
        const updatedUnsavedProjectList = [...pv];

        updatedUnsavedProjectList[index] = project;
        return updatedUnsavedProjectList;
      } else {
        // Add the new object
        return [...pv, project];
      }
    });

    setShowClientProjectSelection(false);
    setProjectEdit({projectId: '', editMode: false});
    showToast(
      t('addEditTeamMember.projectAddSuccess'),
      'success',
      undefined,
      'center'
    );
  };

  const projectEditModeCancelClickHandler = () => {
    setShowClientProjectSelection(false);
  };

  const isInUnsavedMode = () => {
    if (showClientProjectSelection) return true;
    if (!projectEdit) return false;
    if (projectEdit.editMode) return true;

    return false;
  };

  const {showModal} = useModal();
  const checkIsUserExist = (value: string) => {
    if (
      currentTeamMemberclients?.filter((_: string) => _ == value).length > 0
    ) {
      trackGAEvent({
        eventName: GA_EVENT_NAMES.SUB_PAGE_VIEW,
        subPage: {
          subpage_name: t('gaTracking.users.memberAlreadyExistsOnClient'),
          subpage_type: GA_SUBPAGE_TYPE.MODAL,
        },
      });
      showModal({
        title: t('addEditTeamMember.steps.userAlreadyExists'),
        description: '',
        labelOkButton: t('clientDetails.modal.departmentDuplicate.buttonLabel'),
        buttonStyle: 'single',
        onOk: () => {
          trackGAEvent({
            eventName: GA_EVENT_NAMES.CLICK,
            click: {
              name: t('gaTracking.global.close'),
              type: GA_CLICK_TYPES.BUTTON,
              region: GA_REGIONS.MODAL,
              action: t('gaTracking.users.modalClosed'),
            },
            subPage: {
              subpage_name: t('gaTracking.users.memberAlreadyExistsOnClient'),
              subpage_type: GA_SUBPAGE_TYPE.MODAL,
            },
          });
        },
      });
      return true;
    }
    return false;
  };

  useEffect(() => {
    const selectedGroups = projectPermissionGroups.find(
      (item) =>
        item.ItemKey?.toLowerCase() ===
        selectedProjectPermissionId?.toLowerCase()
    );
    const createDocumentPermissionEnabled =
      selectedGroups?.permissions?.some(
        (permission) =>
          permission.permissionCode ===
          ECA_PERMISSION_CODES_CONSTANTS.ECA_CREATE_DOCUMENT
      ) ?? false;
    const ecaPermissionId =
      selectedGroups?.permissions?.find(
        (permission) =>
          permission.type === ECA_PERMISSION_CODES_CONSTANTS.TYPE_ECI &&
          permission.permissionCode !==
            ECA_PERMISSION_CODES_CONSTANTS.ECA_CREATE_DOCUMENT
      )?.permissionId ?? '';
    setSelectedEcaPermission({
      ecaPermissionId,
      createDocumentPermissionEnabled,
    });
  }, [projectPermissionGroups, selectedProjectPermissionId]);

  useEffect(() => {
    //this useeffect is added because after selecting client, there are multiple api calls happening and the focus is lost during the process, so adding the focus back here.
    const isLoading =
      isGetClientProjectsByClientIdLoading || isGetPermissionGroupListLoading;

    if (!isLoading && clientId) {
      setTimeout(() => {
        const roleElement = document.querySelector(
          '[data-testid="aur-test-client-dropdown"] [role="combobox"]'
        ) as HTMLElement | null;
        roleElement?.focus();
      }, 0);
    }
  }, [
    clientId,
    isGetClientProjectsByClientIdLoading,
    isGetPermissionGroupListLoading,
  ]);

  const handleClientAdminModalCancel = () => {
    setAdminRoleSelected(false);
    setShowClientAdminModal(false);
    setClientInfoFormData((prev) => ({
      ...prev,
      role: '',
    }));
  };

  const handleClientAdminModalOkay = () => {
    setShowClientAdminModal(false);
    saveAndContinueHandler();
  };

  return (
    <>
      <FormProvider {...methods}>
        <form
          autoComplete="off"
          data-testid="clientInfoForm__form"
          className="client-info-form"
          onSubmit={(..._arguments) => {
            void handleSubmit(onSubmit)(..._arguments);
          }}
        >
          <AurClientFormFields
            hideProjectAddSection={unsavedProjectList.length > 0}
            control={control}
            roleOptionList={roleOptionList}
            permissionOptionList={clientPermissionGroups}
            projectPermissionOptionList={projectPermissionGroups}
            projects={clientProjects}
            HCprojects={clientHCProjects}
            unsavedProjects={unsavedProjectList}
            onProjectChange={(selectedProjectId) => {
              handleProjectSelection(selectedProjectId);
            }}
            onClientChange={(option) => {
              const budgetConsumptionEnabled =
                option.budgetConsumptionEnabled ?? false;
              setClientId(option.value);
              // Reset Client Administrator state when client changes
              setAdminRoleSelected(false);
              onGetBudgetConsumptionEnabled?.(budgetConsumptionEnabled);
              setClientBudgetEstimatedPermisionEnable(budgetConsumptionEnabled);
              setProjects([]);
              setUnsavedProjectList([]);
              setClientInfoFormData((prev) => {
                const newState =
                  prev.clientId == option.value
                    ? prev
                    : {
                        clientId: option.value,
                        clientName: option.label,
                        project: '',
                        role: '',
                        workspaces: [],
                        permissionGroup: '',
                        permissionGroupId: '',
                        budgetConsumptionEnabled: false,
                      };
                return newState;
              });
              resetBudgetEstimated();
              setWorkspaceOptions([]);
              checkIsUserExist(option.value);
            }}
            onRoleChange={(value: string) => {
              if (value === UserRoleIds.ClientAdmin) {
                setAdminRoleSelected(true);
                setProjects([]);
                setUnsavedProjectList([]);
                setWorkspaceOptions([]);
              } else {
                setAdminRoleSelected(false);
                if (showHCProjects) {
                  setProjects([]);
                  setUnsavedProjectList([]);
                  setWorkspaceOptions([]);
                }
              }
              setClientInfoFormData((prev) => ({
                ...prev,
                role: value,
              }));
              setTeamMemberRole(value);
              setShowHCProjects(false);
            }}
            onPermissionChange={(value: string) => {
              setSelectedClientPermissionId(value);
            }}
            onHCProjectCheckedChange={(checked: boolean) => {
              setShowHCProjects(checked);
              setUnsavedProjectList([]);
            }}
            workspaceOptionList={workspaceOptions}
            isValid={isValid}
            buttonLabel={t('addEditTeamMember.form.addThisProject')}
            buttonVisible={true}
            clientInfoData={clientInfoFormData}
            hasHCProjects={clientHCProjects.length > 0}
            showHCProjects={showHCProjects}
            setClientLoading={setIsGetClientsAsLookupLoading}
            onCreateNewPermissionGroup={(clientId, category) => {
              onCreateNewPermissionGroup(clientId, category);
            }}
          />
        </form>
      </FormProvider>

      <AurDialog
        dialogTitle={t('dialog.areYouSure')}
        popupTitle={t('addEditTeamMember.domainValidationModal.title')}
        popupContent={t('addEditTeamMember.domainValidationModal.content')}
        labelActionOk={t('common.iAmSure')}
        labelActionCancel={t('common.cancel')}
        onOk={handleConfirmDelete}
        onCancel={handleCancelDelete}
        showDialog={openDeleteModal}
        buttonStyle="sideBySide"
        className="with-max-width"
      />
      <AurDialog
        dialogTitle={t('common.justToConfirm')}
        popupTitle={t('addEditTeamMember.promotePopup.title')}
        popupContent={t('addEditTeamMember.promotePopup.content')}
        labelActionOk={t('addEditTeamMember.promotePopup.buttonYes')}
        labelActionCancel={t('addEditTeamMember.promotePopup.buttonCancel')}
        onOk={handleClientAdminModalOkay}
        onCancel={handleClientAdminModalCancel}
        showDialog={showClientAdminModal}
        buttonStyle="sideBySide"
      />

      {!(adminRoleSelected && !showHCProjects) &&
        unsavedProjectList.length > 0 && (
          <>
            <Box className={`${styles.porjectWorkspaceListSectionHeader}`}>
              {t('addEditTeamMember.projectAndWorkspaces')}
            </Box>
            {unsavedProjectList.map((item) => (
              <Box key={item.projectId} className="team-member-accordian">
                <AddTeamMemberAccordion
                  headerTitle={item.projectName}
                  project={item}
                  editButtonClicked={editClickHandler}
                  deleteButtonClicked={deleteClickHandler}
                  editMode={projectEdit}
                  clientProjects={clientProjects}
                  clientHCProjects={clientHCProjects}
                  editModeSubmitClick={addThisProjectClickHandler}
                  unsavedProjects={unsavedProjectList}
                  hideAccordionHeaderButtonGroup={isInUnsavedMode()}
                  isAdmin={adminRoleSelected}
                  showHCProjects={showHCProjects}
                  selectedClientBudgetEstimatedPermisionEnable={
                    clientBudgetEstimatedPermisionEnable
                  }
                  selectedPermissionGroup={selectedProjectPermissionGroup}
                  permissionOptionList={projectPermissionGroups}
                  onCreateNewPermissionGroup={(clientId) => {
                    onCreateNewPermissionGroup(
                      clientId,
                      PERMISSION_GROUP_CATEGORY.PROJECT
                    );
                  }}
                  onPermissionChange={(value: string) => {
                    setSelectedProjectPermissionId(value);
                  }}
                  selectedClientId={clientId}
                />
              </Box>
            ))}

            {showClientProjectSelection && (
              <Box className="add-another-project-workspace">
                <Typography className="add-another-project-header typo-text headline3_size">
                  {t('addEditTeamMember.clientTabAddAnotherProjectTitle')}
                </Typography>
                <ClientProjectSelection
                  clientProjects={clientProjects}
                  clientHCProjects={clientHCProjects}
                  onAddThisProjectClick={addThisProjectClickHandler}
                  unsavedProjects={unsavedProjectList}
                  onCancelClick={projectEditModeCancelClickHandler}
                  isAdmin={adminRoleSelected}
                  showHCProjects={showHCProjects}
                  selectedClientBudgetEstimatedPermisionEnable={
                    clientBudgetEstimatedPermisionEnable
                  }
                  buttonLabel={t('addEditTeamMember.form.addThisProject')}
                  clientInfoData={clientInfoFormData}
                  permissionOptionList={projectPermissionGroups}
                  onCreateNewPermissionGroup={(clientId) => {
                    onCreateNewPermissionGroup(
                      clientId,
                      PERMISSION_GROUP_CATEGORY.PROJECT
                    );
                  }}
                  onPermissionChange={(value: string) => {
                    setSelectedProjectPermissionId(value);
                  }}
                  selectedClientId={clientId}
                  projectTitle={t(
                    'addEditTeamMember.clientTabAddAnotherProjectHeader'
                  )}
                />
              </Box>
            )}

            {!showClientProjectSelection && !projectEdit?.editMode && (
              <Box className="add-another-button">
                <AurButton
                  ariaLabel="Add Another"
                  disabled={false}
                  onClick={() => setShowClientProjectSelection(true)}
                  type="exit"
                  label={t('addEditTeamMember.form.buttonAddAnother')}
                  startIcon={<PlusIcon />}
                />
              </Box>
            )}
          </>
        )}

      <Grid item sm={12} md={12} className="text-right save-button">
        <AurButton
          ariaLabel="Save and Continue"
          disabled={!isSaveButtonEnabled()}
          onClick={() => {
            if (clientInfoFormData?.role === UserRoleIds.ClientAdmin) {
              setShowClientAdminModal(true);
              return;
            }
            saveAndContinueHandler();
          }}
          type="continue"
          label={t('addEditTeamMember.form.saveAndContine')}
          endIcon={<AurIcon icon={IconNames.ChevronRight} />}
          classNames="team-member-save-button"
        />
      </Grid>
      {
        <AurLoadingIndicator
          overlay={true}
          isOpen={
            isGetClientProjectsByClientIdLoading ||
            isGetClientsAsLookupLoading ||
            isGetPermissionGroupListLoading
          }
        />
      }
    </>
  );
};

export default AddClientInfo;
